import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { Card, Form, Input, Radio, Popover, Checkbox, Button } from "antd";
const FormItem = Form.Item;
import { QuestionCircleOutlined } from "@ant-design/icons";
import { withRouter } from "react-router";
import { isEqual, isArray } from "lodash";
import DateRangePicker from "../../common/date-range-picker";
import MultiSelect from "../../common/multi-select";
import FullTextHelp from "../../common/full-text-help";
import { getDistinctValues } from "../../../services/api-repo";
import { keepOnlyJCTVCQueryStrings } from "../../../helpers/url";

const propsToStates = q => ({
  standard: q.standard ? (isArray(q.standard) ? q.standard : [q.standard]) : q.standard,
  fullText: q.full_text,
  documents: q.documents ? q.documents : "latest",
  docId: q.doc_id,
  meeting: q.meeting,
  authors: q.authors,
  status: q.status,
  purpose: q.purpose,
  source: q.source,
  uploadDate: q.upload_date
});

const STANDARD_OPTIONS = [
  { label: "AVC", value: "AVC" },
  { label: "HEVC", value: "HEVC" },
  { label: "VVC", value: "VVC" }
];

class JCTVCToolbar extends Component {
  static propTypes = {
    accessLevel: PropTypes.oneOf(["full", "guest", "public"]).isRequired,
    esUrl: PropTypes.string
  };

  state = {
    ...propsToStates(keepOnlyJCTVCQueryStrings(this.props.location.query))
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.location.query, nextProps.location.query)) {
      this.setState(propsToStates(keepOnlyJCTVCQueryStrings(nextProps.location.query)));
    }
  }

  render() {
    const { accessLevel } = this.props;
    const fullTextLabel = (
      <span>
        {"FULL TEXT "}
        <FullTextHelp />
      </span>
    );
    const resetButton = (
      <Button size="small" onClick={this._resetFilter}>
        {"Reset"}
      </Button>
    );
    const cardTilte = (
      <div>
        <p style={{ margin: 0 }}>AVC, HEVC, VVC</p>
        <p style={{ margin: 0 }}>Standardization</p>
      </div>
    );
    const documentsHelp = (
      <div>
        <p>Cited = 1,322 technical proposals in JCTVC-L1003 (v.32) that were likely incorporated into the HEVC / H.265 specification that are helpful for determining essentiality and prior art.</p>
        <p>Last = Last published version of spec.</p>
        <a href="https://support.unifiedpatents.com/hc/en-us/articles/360010609534?flash_digest=f5d072a60c9edaacc85712cdbd2782330ab014d7">More details here</a>
      </div>
    );
    const documentsLabel = (
      <span>
        {"DOCUMENTS "}
        <Popover placement="rightBottom" title={"Documents Filter"} content={documentsHelp} trigger="click" overlayStyle={{ maxWidth: 300 }}>
          <QuestionCircleOutlined style={{ cursor: "pointer" }} />
        </Popover>
      </span>
    );
    return (
      <Card title={cardTilte} extra={resetButton}>
        <Form layout="vertical">
          <Form.Item label="STANDARD">
            <Checkbox.Group options={STANDARD_OPTIONS} value={this.state.standard || STANDARD_OPTIONS.map(opt => opt.value)} onChange={this._updateState("standard")} />
          </Form.Item>
          <FormItem label={fullTextLabel}>
            <Input
              placeholder="Full Text Search"
              onChange={e => this.setState({ fullText: e.target.value })}
              onPressEnter={this._updateRouter}
              value={this.state.fullText}
              disabled={accessLevel !== "full"}
            />
            {accessLevel !== "full" ? <Link to={{ pathname: "/standards/open/hevc", query: { full_text: "example" } }}> {"Example"} </Link> : null}
          </FormItem>
          <FormItem label={documentsLabel}>
            <Radio.Group defaultValue="latest" buttonStyle="solid" onChange={this._updateDocuments} value={this.state.documents}>
              <Radio.Button value="all">All</Radio.Button>
              <Radio.Button value="latest">Last</Radio.Button>
              <Radio.Button value="cited" disabled={false}>
                Cited
              </Radio.Button>
            </Radio.Group>
          </FormItem>
          {this._renderSelectFormItem("docId", "DOCUMENT ID", false, "e.g. JCTVC-P0073")}
          {this._renderSelectFormItem("meeting", "MEETING", false, "e.g. [P] San Jose (Jan. 2014)")}
          {this._renderSelectFormItem("authors", "AUTHOR", false, "e.g. J. Chen")}
          {this._renderSelectFormItem("status", "STATUS", false, "e.g. Input Document to JCT-VC")}
          {this._renderSelectFormItem("purpose", "PURPOSE", false, "e.g. Proposal")}
          {this._renderSelectFormItem("source", "COMPANY", false, "e.g. Apple Inc.")}
          {this._renderDateFormItem("uploadDate", "SUBMISSION DATE", false)}
        </Form>
      </Card>
    );
  }

  _updateDocuments = e => this.setState({ documents: e.target.value }, this._updateRouter);

  _updateSearchIn = e => this.setState({ searchIn: e.target.value }, this._updateRouter);

  _renderSelectFormItem = (key, label, disabled, placeholder, options, valueRenderer) => {
    const fieldES = {
      docId: "doc_id",
      meeting: "formatted_meeting_string",
      authors: "authors",
      status: "status",
      purpose: "purpose",
      source: "source"
    }[key];
    return (
      <FormItem label={label}>
        <MultiSelect
          value={this.state[key]}
          onChange={this._updateState(key)}
          options={options}
          optionApiCall={
            options
              ? undefined
              : val =>
                getDistinctValues(this.props.esUrl, fieldES, {
                  [fieldES]: val || ""
                })
          }
          optionRender={valueRenderer}
          placeholder={placeholder}
          disabled={disabled}
        />
      </FormItem>
    );
  };

  _renderDateFormItem = (key, label, disabled) => {
    return (
      <FormItem label={label}>
        <DateRangePicker value={this.state[key]} onChange={this._updateState(key)} disabled={disabled} />
      </FormItem>
    );
  };

  _runAfterTyping = (func, interval = 800) => {
    if (this._timeout) clearTimeout(this._timeout);
    this._timeout = setTimeout(func, interval);
  };

  _updateState = field => {
    return val => {
      this.setState({ [field]: val }, this._updateRouter);
    };
  };

  _updateRouter = () => {
    this.props.router.push({
      pathname: this.props.location.pathname,
      query: {
        standard: this.state.standard && this.state.standard.length === STANDARD_OPTIONS.length ? undefined : this.state.standard,
        full_text: this.state.fullText,
        documents: this.state.documents,
        doc_id: this.state.docId,
        meeting: this.state.meeting,
        authors: this.state.authors,
        status: this.state.status,
        purpose: this.state.purpose,
        source: this.state.source,
        upload_date: this.state.uploadDate
      }
    });
  };

  _resetFilter = () =>
    this.setState(
      {
        standard: null,
        fullText: null,
        documents: null,
        docId: null,
        meeting: null,
        authors: null,
        status: null,
        purpose: null,
        source: null,
        uploadDate: null
      },
      () => {
        this.props.router.push({
          pathname: this.props.location.pathname,
          query: null
        });
      }
    );
}

export default withRouter(JCTVCToolbar);
